import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Minus, Plus, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_PRODUCT_IMAGE = "https://nivilabs.bionivid.com/resources/images/assest.static.default.product.png";

export default function InquiryCartDrawer() {
  const { items, remove, setQty, clear, open, setOpen } = useCart();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
    organization: user?.organization || "", address: user?.address || "", notes: "",
  });

  React.useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name, email: user.email, phone: user.phone || "", organization: user.organization || "", address: user.address || "" }));
  }, [user]);

  const submit = async () => {
    if (!form.notes.trim()) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          catalogNumber: i.catalogNumber,
          quantity: i.quantity,
          image: i.image || DEFAULT_PRODUCT_IMAGE,
          selectedCatalogNumber: i.selectedCatalogNumber,
          selectedPackSize: i.selectedPackSize,
        })),
      };
      await api.post("/inquiries", payload);
      toast.success("Inquiry submitted! Our team will reach out within 24 hours.");
      clear();
      // setStep(1);
      setOpen(false);
      nav("/inquiry-success");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col bg-background" data-testid="inquiry-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">
            Inquiry Cart
          </SheetTitle>
          <p className="text-xs text-muted-foreground">
            Add products and submit a Request-for-Quote. No payment required.
          </p>
        </SheetHeader>

          <div className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="cart-items">
            {items.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <p className="text-sm">Your inquiry cart is empty.</p>
                <Button className="mt-4" onClick={() => { setOpen(false); nav("/products"); }} data-testid="browse-products-btn">
                  Browse products
                </Button>
              </div>
            ) : (
              items.map((i) => (
                <div key={i.product_id} className="flex gap-3 p-3 bg-white rounded-lg border border-gray-100" data-testid={`cart-item-${i.catalogNumber}`}>
                  <img src={i.image || DEFAULT_PRODUCT_IMAGE} alt={i.name} className="w-16 h-16 rounded object-cover" />
                  <div className="flex-1">
                    <div className="font-medium text-sm leading-tight">{i.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Catalog No: {i.catalogNumber}</div>
                    {i.selectedPackSize && (
                      <div className="text-xs text-muted-foreground mt-1">Pack size: {i.selectedPackSize}</div>
                    )}
                    {i.selectedCatalogNumber && i.selectedCatalogNumber !== i.catalogNumber && (
                      <div className="text-xs text-muted-foreground mt-1">Selected catalog: {i.selectedCatalogNumber}</div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={() => setQty(i.line_id, i.quantity - 1)} className="w-7 h-7 rounded-md border hover:bg-muted" data-testid={`qty-minus-${i.catalogNumber}`}>
                        <Minus className="w-3 h-3 m-auto" />
                      </button>
                      <input
                        type="number"
                        min={1}
                        value={i.quantity}
                        onChange={(e) => setQty(i.line_id, parseInt(e.target.value || "1", 10))}
                        className="w-12 h-7 text-center border rounded-md text-sm"
                        data-testid={`qty-input-${i.catalogNumber}`}
                      />
                      <button onClick={() => setQty(i.line_id, i.quantity + 1)} className="w-7 h-7 rounded-md border hover:bg-muted" data-testid={`qty-plus-${i.catalogNumber}`}>
                        <Plus className="w-3 h-3 m-auto" />
                      </button>
                      <button onClick={() => remove(i.line_id)} className="ml-auto text-destructive hover:opacity-80" data-testid={`remove-${i.catalogNumber}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
            {items.length > 0 && (
              <div className="mt-4">
                <Field label="Notes / Special requirements">
                  <Textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        notes: e.target.value,
                      })
                    }
                    data-testid="inq-notes"
                    placeholder="Any special requirements, delivery instructions, bulk quantity requests, etc."
                  />
                </Field>
              </div>
            )}
          </div>

        {items.length > 0 && (
          <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
            {items.length} item(s) will be submitted with your inquiry.
          </div>
        )}

        <SheetFooter className="border-t pt-4">
          <Button
            className="w-full bg-primary hover:bg-primary/90 h-11"
            disabled={items.length === 0 || submitting}
            onClick={() => {
              if (!user) {
                nav("/login", {
                  state: { redirectTo: "inquiry" },
                });
                setOpen(false);
                return;
              }

              submit();
            }}
            data-testid="proceed-to-form"
          >
            {submitting ? (
              "Submitting..."
            ) : user ? (
              "Submit Inquiry"
            ) : (
              <>
                Continue to Inquiry
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-foreground/80">{label}</Label>
    {children}
  </div>
);
